import { Provider } from '@angular/core';
import { providerFeature, ProviderFeature } from '@fmnts/common';
import {
  DECLARATIVE_FORMS_TEMPLATE_BUILDER_TOKEN,
  DECLARATIVE_FORMS_VALIDATOR_TOKEN,
  GRAPH_NODE_BUILDER_TOKEN,
} from '@fmnts/declarative-forms';
import { declarativeFormFieldPath } from '@formunauts/shared/declarative-forms/domain';
import * as Arr from 'effect/Array';
import * as Struct from 'effect/Struct';
import {
  DECLARATIVE_FORM_AMOUNT_FEATURE_CONFIG,
  DeclarativeFormAmountFeatureConfig,
} from './declarative-form-amount.tokens';
import { DonationAmountGraphNodeBuilder } from './donation-amount-node-builder.service';
import { DonationAmountTemplateService } from './donation-amount-template.service';
import { DonationAmountValidationService } from './donation-amount-validation.service';

export enum DeclarativeFormAmountFeatureKind {
  Graphs,
  Templates,
  Validators,
}

export type DeclarativeFormAmountFeature<
  TKind extends DeclarativeFormAmountFeatureKind,
> = ProviderFeature<TKind>;

const { make: makeFeature } =
  providerFeature<DeclarativeFormAmountFeatureKind>();

export function provideDeclarativeFormAmountFeature(
  ...features: DeclarativeFormAmountFeature<DeclarativeFormAmountFeatureKind>[]
): Provider {
  return [
    {
      provide: DECLARATIVE_FORM_AMOUNT_FEATURE_CONFIG,
      useFactory: (): DeclarativeFormAmountFeatureConfig => ({
        paths: {
          amountField: declarativeFormFieldPath.donationAmount,
          amountPerIntervalField:
            declarativeFormFieldPath.donationAmountPerInterval,
          intervalField: declarativeFormFieldPath.directDebitInterval,
        },
      }),
    },
    ...Arr.flatMap(features, Struct.get('providers')),
  ];
}

export function withAmountGraphNodes(): DeclarativeFormAmountFeature<DeclarativeFormAmountFeatureKind.Graphs> {
  return makeFeature(DeclarativeFormAmountFeatureKind.Graphs, [
    {
      provide: GRAPH_NODE_BUILDER_TOKEN,
      useExisting: DonationAmountGraphNodeBuilder,
      multi: true,
    },
  ]);
}

export function withAmountTemplates(): DeclarativeFormAmountFeature<DeclarativeFormAmountFeatureKind.Templates> {
  return makeFeature(DeclarativeFormAmountFeatureKind.Templates, [
    {
      provide: DECLARATIVE_FORMS_TEMPLATE_BUILDER_TOKEN,
      useClass: DonationAmountTemplateService,
      multi: true,
    },
  ]);
}

export function withAmountValidators(): DeclarativeFormAmountFeature<DeclarativeFormAmountFeatureKind.Validators> {
  return makeFeature(DeclarativeFormAmountFeatureKind.Validators, [
    {
      provide: DECLARATIVE_FORMS_VALIDATOR_TOKEN,
      useExisting: DonationAmountValidationService,
      multi: true,
    },
  ]);
}
